"use client";

import Header from "@/components/Header";
import Link from "next/link";
import {
  Building2,
  FileCheck,
  RefreshCw,
  ClipboardList,
  Shield,
  Clock,
  ChevronRight,
  Leaf,
  MapPin,
  Phone,
  Mail,
} from "lucide-react";

export default function Home() {
  return (
    <div className="min-h-screen">
      <Header />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-primary via-primary-dark to-green-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-64 h-64 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-20 w-96 h-96 bg-green-300 rounded-full blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 relative z-10">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 mb-4">
              <Leaf size={20} className="text-green-300" />
              <span className="text-green-300 text-sm font-medium uppercase tracking-wider">
                Instituto de Qualidade Ambiental
              </span>
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold leading-tight mb-6">
              Licenciamento Digital
              <br />
              <span className="text-green-300">Autodeclaratório</span>
            </h1>
            <p className="text-lg text-green-100 mb-8 leading-relaxed max-w-2xl">
              Plataforma de licenciamento eletrônico do IQUAMA para emissão de
              alvarás de construção no município de Aracati. Solicite seu alvará
              de forma online, a qualquer hora do dia.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                href="/solicitacao/emissao"
                className="bg-white text-primary px-6 py-3 rounded-xl font-semibold hover:bg-green-50 transition flex items-center gap-2 shadow-lg"
              >
                <FileCheck size={20} />
                Solicitar Alvará
              </Link>
              <Link
                href="/solicitacao/renovacao"
                className="border-2 border-white text-white px-6 py-3 rounded-xl font-semibold hover:bg-white/10 transition flex items-center gap-2"
              >
                <RefreshCw size={20} />
                Renovar Alvará
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Info Cards */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-12 relative z-20">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            {
              icon: Clock,
              title: "100% Online",
              desc: "Solicite seu alvará a qualquer momento, sem necessidade de ir presencialmente ao IQUAMA.",
            },
            {
              icon: Shield,
              title: "Autodeclaratório",
              desc: "O processo é baseado na responsabilidade do requerente e profissionais técnicos envolvidos.",
            },
            {
              icon: ClipboardList,
              title: "Monitoramento",
              desc: "Após emissão, há monitoramento da documentação com prazo para correções se necessário.",
            },
          ].map((item) => (
            <div
              key={item.title}
              className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100 hover:shadow-2xl transition"
            >
              <div className="w-12 h-12 bg-primary-light rounded-xl flex items-center justify-center mb-4">
                <item.icon size={24} className="text-primary" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
              <p className="text-sm text-gray-500 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Services */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-3">
            Serviços Disponíveis
          </h2>
          <p className="text-gray-500 max-w-xl mx-auto">
            Acesse os serviços de licenciamento ambiental e urbano do IQUAMA
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          <Link
            href="/solicitacao/emissao"
            className="group bg-white rounded-2xl p-6 border border-gray-200 hover:border-primary hover:shadow-lg transition-all"
          >
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 bg-primary-light rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-primary group-hover:text-white transition">
                <Building2 size={28} className="text-primary group-hover:text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1 flex items-center gap-2">
                  Emissão de Alvará de Construção
                  <ChevronRight
                    size={16}
                    className="text-gray-300 group-hover:text-primary transition"
                  />
                </h3>
                <p className="text-sm text-gray-500">
                  Solicite um novo alvará de construção para seu empreendimento
                  no município de Aracati.
                </p>
                <div className="mt-3 flex gap-2">
                  <span className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded-full">
                    Validade: 5 anos
                  </span>
                  <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-full">
                    Online
                  </span>
                </div>
              </div>
            </div>
          </Link>

          <Link
            href="/solicitacao/renovacao"
            className="group bg-white rounded-2xl p-6 border border-gray-200 hover:border-secondary hover:shadow-lg transition-all"
          >
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 bg-secondary-light rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-secondary group-hover:text-white transition">
                <RefreshCw size={28} className="text-secondary group-hover:text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1 flex items-center gap-2">
                  Renovação de Alvará de Construção
                  <ChevronRight
                    size={16}
                    className="text-gray-300 group-hover:text-secondary transition"
                  />
                </h3>
                <p className="text-sm text-gray-500">
                  Renove um alvará de construção já existente que esteja
                  vencido ou próximo do vencimento.
                </p>
                <div className="mt-3 flex gap-2">
                  <span className="text-xs bg-amber-50 text-amber-700 px-2 py-1 rounded-full">
                    Requer alvará anterior
                  </span>
                </div>
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* How it Works */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">
              Como Funciona
            </h2>
            <p className="text-gray-500">Fluxo simplificado para emissão do alvará</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 max-w-5xl mx-auto">
            {[
              { step: 1, title: "Cadastro e Login", desc: "Acesse a plataforma com seu CPF e senha" },
              { step: 2, title: "Preenchimento", desc: "Informe dados do empreendimento e responsáveis" },
              { step: 3, title: "Documentação", desc: "Anexe projetos, ARTs/RRTs e documentos" },
              { step: 4, title: "Emissão", desc: "Confirme, pague a taxa e receba seu alvará" },
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="w-16 h-16 bg-primary-light text-primary rounded-2xl flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                  <span className="font-bold text-sm">IQ</span>
                </div>
                <div>
                  <h3 className="font-bold">IQUAMA</h3>
                  <p className="text-xs text-gray-400">
                    Instituto de Qualidade Ambiental de Aracati
                  </p>
                </div>
              </div>
              <p className="text-sm text-gray-400">
                Responsável pelo licenciamento ambiental e urbanístico do
                município de Aracati-CE.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Links Úteis</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>
                  <Link href="/solicitacao/emissao" className="hover:text-white transition">
                    Solicitar Alvará
                  </Link>
                </li>
                <li>
                  <Link href="/solicitacao/renovacao" className="hover:text-white transition">
                    Renovar Alvará
                  </Link>
                </li>
                <li>
                  <Link href="/dashboard" className="hover:text-white transition">
                    Consultar Processos
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Contato</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li className="flex items-center gap-2">
                  <MapPin size={14} />
                  Aracati, Ceará
                </li>
                <li className="flex items-center gap-2">
                  <Phone size={14} />
                  (88) 0000-0000
                </li>
                <li className="flex items-center gap-2">
                  <Mail size={14} />
                  contato@iquama.ce.gov.br
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-xs text-gray-500">
            &copy; {new Date().getFullYear()} IQUAMA - Instituto de Qualidade
            Ambiental de Aracati. Todos os direitos reservados.
          </div>
        </div>
      </footer>
    </div>
  );
}
